/**
 * Chat de soporte (widget flotante).
 *
 * El widget lo sirve la app de chat: `<origen>/widget.js` inyecta un botón
 * "Ayuda" abajo a la derecha (Shadow DOM propio + iframe lazy a `<origen>/embed`,
 * que recién carga en el primer click). Lo colgamos de <body>, fuera de #app,
 * así los cambios de ruta del SPA no lo destruyen.
 *
 * Al iframe le pasamos las credenciales por query param —token, projectId,
 * userId y name— porque el chat las usa para leer audiencias, clientes y
 * personalizaciones del proyecto activo. Sin token y projectId arranca en modo
 * degradado (sólo responde documentación), así que directamente no lo mostramos
 * hasta tenerlas: ver isSupportChatEnabled. Nada de botón en el login, en
 * select-company ni mientras el nav-bar pide elegir proyecto.
 *
 * `userId` es la identidad del historial del chat (viaja al backend como header
 * x-gp-user-id, igual que el projectId): con él la conversación sigue al usuario
 * del admin. Es opcional —si falta, el cliente del chat genera uno propio y lo
 * guarda en localStorage, historial por navegador— pero acá siempre lo tenemos.
 *
 * `syncSupportChat` corre en cada navegación (ver main.js) y deja el widget en
 * el estado que corresponde: lo monta, lo desmonta, o recarga el iframe cuando
 * las credenciales cambiaron.
 *
 * Minimizar/maximizar el panel ya lo resuelve widget.js por su cuenta (escucha
 * los postMessage del iframe y le contesta `frame-state`), no hay nada que
 * manejar de este lado.
 *
 * Siempre arranca cerrado: solo el botón. El panel lo abre el usuario, y un
 * reload (cambiar de contexto/proyecto o de idioma) lo devuelve al botón.
 *
 * El origen es configurable en runtime: VUE_APP_SUPPORT_CHAT_URL de config.json
 * (el container lo genera por ambiente, ver Dockerfile). DEFAULT_ORIGIN es el
 * fallback para cuando el deploy no setea SUPPORT_CHAT_URL.
 */

const DEFAULT_ORIGIN =
  "https://gopersonal-support-prod.calvinruntime.net";

const MARKER = "data-gopersonal-support-chat";

// Elemento que widget.js cuelga de <body> con el Shadow DOM del chat adentro.
const HOST_SELECTOR = "[data-gopersonal-chat]";

// Flag con el que widget.js evita montarse dos veces.
const WIDGET_GLOBAL = "__gopersonalChat";

// El chat ignora el nombre si se pasa de acá, así que lo cortamos nosotros.
const MAX_NAME_LENGTH = 32;

export function getSupportChatOrigin() {
  const configured =
    (typeof window !== "undefined" &&
      window.config &&
      window.config.VUE_APP_SUPPORT_CHAT_URL) ||
    "";

  return String(configured || DEFAULT_ORIGIN).trim().replace(/\/+$/, "");
}

/**
 * Lo que el chat necesita para operar sobre el proyecto, de las mismas fuentes
 * que usa el resto del admin: el token vive en localStorage (compartido entre
 * pestañas) y el proyecto en sessionStorage (por pestaña, ver companySession).
 *
 * `token` es el JWT crudo, sin el prefijo `Bearer `, igual que lo guardan los
 * services antes de armar el header. `name` es sólo para el saludo.
 */
function readCredentials() {
  if (typeof window === "undefined") {
    return { token: "", projectId: "", userId: "", name: "" };
  }

  let stored = null;
  let projectId = "";

  try {
    stored = JSON.parse(window.localStorage.getItem("auth.currentUser"));
    projectId = window.sessionStorage.getItem("current_project") || "";
  } catch (e) {
    stored = null;
  }

  const rawName =
    stored && stored.user && typeof stored.user.name === "string"
      ? stored.user.name.trim()
      : "";

  return {
    token: (stored && stored.token) || "",
    projectId,
    userId: (stored && stored.user && stored.user._id) || "",
    name: rawName.slice(0, MAX_NAME_LENGTH),
  };
}

/**
 * El chat sólo sirve con la sesión armada. El proyecto además tiene que ser de
 * la compañía activa del token, que es la ÚLTIMA elegida en cualquier pestaña:
 * el chat no manda la compañía de la pestaña (header x-company, ver
 * helpers/companySession), así que en una pestaña que quedó en otra compañía
 * ve listados vacíos hasta que reciba la compañía por parámetro.
 */
export function isSupportChatEnabled() {
  const { token, projectId } = readCredentials();
  return !!token && !!projectId;
}

// Identifica un juego de credenciales, para detectar cuándo el iframe quedó viejo.
function credentialsKey({ token, projectId, userId }) {
  return `${token}|${projectId}|${userId}`;
}

function withCredentials(url, credentials) {
  try {
    const parsed = new URL(url, window.location.href);
    if (credentials.token) parsed.searchParams.set("token", credentials.token);
    if (credentials.projectId) parsed.searchParams.set("projectId", credentials.projectId);
    if (credentials.userId) parsed.searchParams.set("userId", credentials.userId);
    if (credentials.name) parsed.searchParams.set("name", credentials.name);
    return parsed.href;
  } catch (e) {
    return url;
  }
}

// Credenciales con las que cargó el iframe, null mientras el panel no se abrió.
let embeddedCredentials = null;

function getEmbedFrame() {
  const host = document.querySelector(HOST_SELECTOR);
  return host && host.shadowRoot ? host.shadowRoot.querySelector("iframe") : null;
}

/**
 * widget.js sabe pasarle `?name=` al iframe (data-name), pero no el token ni el
 * proyecto, y arma la URL por su cuenta (`<origen>/embed`, lazy en el primer
 * click). Así que interceptamos el set de `src` sobre ese iframe —su shadow root
 * es `open`— y le colgamos las credenciales al vuelo, recién ahí, para que
 * viajen las vigentes al momento de abrir el panel.
 *
 * Si el widget cambia y no encontramos el iframe, el chat carga igual pero en
 * modo degradado: responde documentación, sin acceso a los datos del proyecto.
 *
 * El día que widget.js acepte data-token / data-project-id, esto se borra.
 */
function interceptEmbedUrl() {
  const frame = getEmbedFrame();
  if (!frame) return;

  Object.defineProperty(frame, "src", {
    configurable: true,
    get() {
      const value = frame.getAttribute("src") || "";
      return value ? new URL(value, window.location.href).href : "";
    },
    set(value) {
      const credentials = readCredentials();
      embeddedCredentials = credentialsKey(credentials);
      frame.setAttribute("src", withCredentials(String(value), credentials));
    },
  });
}

/**
 * El launcher de widget.js es una píldora "Ayuda" flotante abajo a la derecha,
 * justo donde el admin pone las action bars fijas (Cancelar/Guardar de drawers
 * y wizards), y con su z-index las tapa. Hasta que widget.js soporte configurar
 * la posición, lo reestilamos desde acá —mismo truco que interceptEmbedUrl: el
 * shadow root es open— como pestaña anclada al borde derecho a media altura,
 * solo ícono, con el label como tooltip al hover.
 *
 * Si el widget cambia los classnames, el estilo no matchea y queda el look
 * default: molesto pero funcional, nunca roto.
 */
function restyleLauncher() {
  const host = document.querySelector(HOST_SELECTOR);
  if (!host || !host.shadowRoot) return;

  // Globo de chat → sparkles "AI" (dos estrellas de 4 puntas, como el patrón
  // de asistentes tipo Insider One). Dibujado acá, en blanco sobre el gradiente.
  const launcherSvg = host.shadowRoot.querySelector(".gp-launcher svg");
  if (launcherSvg && !launcherSvg.hasAttribute("data-admin-sparkles")) {
    launcherSvg.setAttribute("data-admin-sparkles", "");
    launcherSvg.setAttribute("viewBox", "0 0 24 24");
    launcherSvg.setAttribute("width", "22");
    launcherSvg.setAttribute("height", "22");
    launcherSvg.innerHTML =
      '<path fill="currentColor" d="M10 7 Q11.2 12.8 17 14 Q11.2 15.2 10 21 Q8.8 15.2 3 14 Q8.8 12.8 10 7 Z"/>' +
      '<path fill="currentColor" d="M17.5 3 Q18.2 5.8 21 6.5 Q18.2 7.2 17.5 10 Q16.8 7.2 14 6.5 Q16.8 5.8 17.5 3 Z"/>';
  }

  if (host.shadowRoot.querySelector("style[data-admin-restyle]")) return;

  const style = document.createElement("style");
  style.setAttribute("data-admin-restyle", "");
  style.textContent = `
    .gp-launcher {
      right: 0;
      bottom: auto;
      top: calc(60% - 24px);
      width: 46px;
      height: 48px;
      padding: 0;
      justify-content: center;
      border-radius: 14px 0 0 14px;
      background: linear-gradient(160deg, #7a5ae0, #5e40bf);
      box-shadow: 0 6px 20px -8px rgba(94, 64, 191, .6);
    }
    .gp-launcher span { display: none; }
    .gp-launcher:hover { transform: translateX(-3px); }
    /* Twinkle: los sparkles pulsan de tamaño, desfasados entre sí */
    .gp-launcher svg path {
      transform-box: fill-box;
      transform-origin: center;
      animation: gp-twinkle 2.6s ease-in-out infinite;
    }
    .gp-launcher svg path:last-child {
      animation-duration: 2.2s;
      animation-delay: -1.3s;
    }
    @keyframes gp-twinkle {
      0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
      50% { transform: scale(.55) rotate(-15deg); opacity: .75; }
    }
    /* Tooltip con el aria-label (el mismo data-label i18n que le pasa main.js) */
    .gp-launcher::after {
      content: attr(aria-label);
      position: absolute;
      right: calc(100% + 12px);
      top: 50%;
      transform: translateY(-50%);
      background: #212529;
      color: #fff;
      font-size: 13px;
      font-weight: 500;
      line-height: 1;
      padding: 8px 12px;
      border-radius: 8px;
      white-space: nowrap;
      opacity: 0;
      transition: opacity .15s ease;
      pointer-events: none;
    }
    .gp-launcher::before {
      content: "";
      position: absolute;
      right: calc(100% + 2px);
      top: 50%;
      transform: translateY(-50%);
      border-style: solid;
      border-width: 5px 0 5px 6px;
      border-color: transparent transparent transparent #212529;
      opacity: 0;
      transition: opacity .15s ease;
      pointer-events: none;
    }
    .gp-launcher:hover::after,
    .gp-launcher:hover::before { opacity: 1; }
    @media (prefers-reduced-motion: reduce) {
      .gp-launcher::after,
      .gp-launcher::before { transition: none; }
      .gp-launcher svg path { animation: none; }
    }
  `;
  host.shadowRoot.appendChild(style);
}

/**
 * El iframe lee las credenciales una sola vez, al montar: si cambiaron hay que
 * recargarlo. En el admin casi todo lo que las cambia recarga la página entera
 * (elegir proyecto y cambiar de compañía hacen window.open("/","_self")), pero
 * el token puede renovarse sin eso y el chat empezaría a comer 401.
 */
function reloadEmbedIfStale() {
  // Panel nunca abierto: widget.js todavía no le puso src y lo va a hacer con
  // las credenciales del momento.
  if (!embeddedCredentials) return;

  const frame = getEmbedFrame();
  if (!frame) return;
  if (credentialsKey(readCredentials()) === embeddedCredentials) return;

  // El setter interceptado le cuelga las credenciales nuevas.
  frame.src = `${getSupportChatOrigin()}/embed`;
}

export function mountSupportChat({ label } = {}) {
  if (typeof document === "undefined") return;
  // También en los dominios white-label (personalization.innew.ai): el chat sólo
  // habla de Calvin, sin la marca gopersonal.
  if (!isSupportChatEnabled()) return;
  if (document.querySelector(`script[${MARKER}]`)) return;

  const origin = getSupportChatOrigin();
  if (!origin) return;

  const inject = () => {
    const script = document.createElement("script");
    script.src = `${origin}/widget.js`;
    script.setAttribute(MARKER, "");
    if (label) script.setAttribute("data-label", label);
    script.onload = () => {
      interceptEmbedUrl();
      restyleLauncher();
      applyLauncherHidden();
      // El widget recién existe ahora. La pantalla se informó mientras el
      // script bajaba y se perdió, así que se reenvía.
      setSupportChatScreen(reportedScreen);
    };
    script.onerror = () => {
      // Chat caído o inalcanzable: el admin sigue igual, sin botón.
    };
    document.body.appendChild(script);
  };

  if (document.body) inject();
  else document.addEventListener("DOMContentLoaded", inject, { once: true });
}

/**
 * Saca el widget de la página: el script inyectado y el host que armó widget.js
 * (con su Shadow DOM adentro). Lo usamos al desloguear y al quedarnos sin
 * proyecto.
 */
export function unmountSupportChat() {
  if (typeof document === "undefined") return;

  const script = document.querySelector(`script[${MARKER}]`);
  if (script && script.parentNode) script.parentNode.removeChild(script);

  const host = document.querySelector(HOST_SELECTOR);
  if (host && host.parentNode) host.parentNode.removeChild(host);

  // widget.js se corta solo con este flag; sin borrarlo, reinyectar el script
  // no vuelve a crear el widget.
  if (typeof window !== "undefined") delete window[WIDGET_GLOBAL];

  embeddedCredentials = null;
}

/**
 * Abre el panel del chat apuntado a una variante de personalización: es el
 * "Ask Calvin" vía chat. `personalization` es el preset que arma variants.vue
 * (contentId/targetId/variantId/contentKey/nombres) más testUrl y draft; viaja
 * por postMessage al iframe (widget.js lo encola hasta que el embed avisa
 * `ready`, porque el iframe carga lazy) y el chat lo usa para ofrecerle al
 * modelo la herramienta que crea la tarea de Calvin.
 *
 * `prompt` (opcional) queda escrito en la caja del chat para que el usuario lo
 * revise y lo mande; un chat viejo lo ignora.
 *
 * Devuelve false cuando el widget no está o todavía no soporta openWith (chat
 * viejo cacheado, white-label, sesión sin proyecto): el caller decide el
 * fallback — hoy, el drawer clásico de tareas.
 */
export function askCalvinInSupportChat(personalization, prompt) {
  if (typeof window === "undefined") return false;
  const chat = window[WIDGET_GLOBAL];
  if (!chat || typeof chat.openWith !== "function") return false;
  // prompt (opcional): pedido que el chat deja escrito en la caja, sin mandarlo.
  chat.openWith(prompt ? { personalization, prompt } : { personalization });
  // Entrando por Ask Calvin el chat es la tarea principal, no un panelito de
  // ayuda: se abre maximizado (el usuario puede restaurarlo desde el panel).
  if (typeof chat.maximize === "function") chat.maximize(true);
  return true;
}

/**
 * Oculta/muestra la píldora flotante del chat ("Ayuda"). Para pantallas que
 * ya tienen su botón Ask Calvin propio: tener los dos a la vez confunde. Solo
 * toca el launcher — el panel abierto por Ask Calvin sigue funcionando igual.
 * Mismo truco que restyleLauncher: el shadow root del widget es open.
 */
// Estado deseado del launcher. Vive acá (módulo) y no en el DOM: el widget
// puede montarse DESPUÉS de que la pantalla pidió ocultarlo (deep link al
// editor, script lento) o volver a montarse en una navegación, y en los dos
// casos hay que reaplicarlo. Ver applyLauncherHidden.
let launcherHidden = false;
let launcherHiddenRetry = null;
let launcherHiddenObserver = null;
let observedLauncherRoot = null;

const HIDE_STYLE_MARKER = "data-admin-launcher-hidden";

function ensureLauncherHiddenObserver() {
  if (
    typeof document === "undefined" ||
    typeof window === "undefined" ||
    typeof window.MutationObserver === "undefined" ||
    launcherHiddenObserver
  ) return;

  const onMutation = () => {
    applyLauncherHidden();
    observeLauncherRoot();
  };

  launcherHiddenObserver = new window.MutationObserver(onMutation);
  const root = document.body || document.documentElement;
  if (root) launcherHiddenObserver.observe(root, { childList: true });
  observeLauncherRoot();
}

function stopLauncherHiddenObserver() {
  if (!launcherHiddenObserver) return;
  launcherHiddenObserver.disconnect();
  launcherHiddenObserver = null;
  observedLauncherRoot = null;
}

function observeLauncherRoot() {
  if (!launcherHiddenObserver || typeof document === "undefined") return;
  const host = document.querySelector(HOST_SELECTOR);
  const root = host && host.shadowRoot;
  if (!root || root === observedLauncherRoot) return;
  observedLauncherRoot = root;
  launcherHiddenObserver.observe(root, { childList: true, subtree: true });
}

/**
 * Aplica `launcherHidden` al widget si ya existe. Se hace con una regla de
 * estilo en el shadow root, no con style inline sobre el botón: widget.js
 * togglea `launcher.hidden` al abrir/cerrar el panel y puede recrear el botón,
 * y una regla con !important sobrevive a las dos cosas.
 * Devuelve true si pudo aplicar (host y shadow root presentes).
 */
function applyLauncherHidden() {
  if (typeof document === "undefined") return false;
  const host = document.querySelector(HOST_SELECTOR);
  if (!host || !host.shadowRoot) return false;
  const existing = host.shadowRoot.querySelector(`style[${HIDE_STYLE_MARKER}]`);
  if (launcherHidden && !existing) {
    const style = document.createElement("style");
    style.setAttribute(HIDE_STYLE_MARKER, "");
    style.textContent = ".gp-launcher { display: none !important; }";
    host.shadowRoot.appendChild(style);
  } else if (!launcherHidden && existing) {
    existing.remove();
  }
  return true;
}

export function setSupportChatLauncherHidden(hidden) {
  launcherHidden = !!hidden;
  if (launcherHiddenRetry) {
    clearInterval(launcherHiddenRetry);
    launcherHiddenRetry = null;
  }

  if (launcherHidden) ensureLauncherHiddenObserver();
  else stopLauncherHiddenObserver();

  if (applyLauncherHidden() || !launcherHidden) return;
  // El widget todavía no está: reintentar un rato (widget.js se inyecta en la
  // primera navegación y arma el shadow root al cargar). Además queda un
  // MutationObserver activo mientras el estado deseado sea oculto: si el script
  // carga tarde, si el router lo remonta, o si widget.js recrea el launcher, la
  // regla se vuelve a aplicar sin que la pantalla tenga que pedirlo otra vez.
  let attempts = 0;
  launcherHiddenRetry = setInterval(() => {
    attempts += 1;
    if (applyLauncherHidden() || attempts >= 40 || !launcherHidden) {
      clearInterval(launcherHiddenRetry);
      launcherHiddenRetry = null;
    }
  }, 250);
}

/**
 * Ask Calvin de componentes de email: abre el chat con el estado VIVO del
 * editor (html/css sin guardar, variables, un item de muestra). Del lado del
 * chat esto habilita la tool que crea una tarea de Calvin ruteada al subagente
 * email-component, que edita y guarda el template vía API; el contexto sólo
 * existe cuando viaja desde esta pantalla.
 */
export function askEmailComponentInSupportChat(emailComponent) {
  if (typeof window === "undefined") return false;
  const chat = window[WIDGET_GLOBAL];
  if (!chat || typeof chat.openWith !== "function") return false;
  chat.openWith({ emailComponent });
  return true;
}

/**
 * Ask Calvin del editor Mailcraft (botón ✨ de la toolbar, vía
 * aiBuild.ask): abre el chat en una conversación NUEVA con el diseño actual,
 * el spec del formato, los merge tags y los tipos de bloque — todo provisto
 * por la librería, que es la dueña del formato. El tuneo es una tool nativa
 * del chat que reescribe el design JSON, y el resultado vuelve por el evento
 * `gopersonal-chat:apply-mailcraft-design` (ver widget.js) para aplicarse en
 * el canvas como un paso de undo: acá nadie escribe nada a la API.
 */
export function askMailcraftInSupportChat(mailcraftDesign) {
  if (typeof window === "undefined") return false;
  const chat = window[WIDGET_GLOBAL];
  if (!chat || typeof chat.openWith !== "function") return false;
  chat.openWith({ mailcraftDesign });
  return true;
}

/**
 * Ask Calvin de integraciones de productos: abre el chat apuntado a la fila
 * del feed (id, nombre, tipo). Del lado del chat habilita la tool que crea la
 * tarea de Calvin ruteada al subagente `integrations` del workspace
 * gopersonal, que diagnostica/ajusta el feed vía API.
 */
export function askIntegrationInSupportChat(integration) {
  if (typeof window === "undefined") return false;
  const chat = window[WIDGET_GLOBAL];
  if (!chat || typeof chat.openWith !== "function") return false;
  chat.openWith({ integration });
  return true;
}

/**
 * "Armar con Calvin" del explorador de audiencias: abre el chat en una
 * conversación nueva, dedicada a armar una audiencia.
 *
 * No crea ninguna tarea de Calvin — acá contesta el chat en el momento, con sus
 * propias tools: lee los campos disponibles del proyecto, arma las condiciones
 * y las mide contra el duck antes de proponerlas. Por ahora sólo se ofrece con
 * la pantalla vacía, así que no viaja ninguna condición: es el caso de la hoja
 * en blanco. Cuando se agregue el botón de la barra de guardar, acá va a viajar
 * también el árbol que el usuario tenga armado.
 */
export function askAudienceInSupportChat() {
  if (typeof window === "undefined") return false;
  const chat = window[WIDGET_GLOBAL];
  if (!chat || typeof chat.openWith !== "function") return false;
  chat.openWith({ audienceBuilder: { origin: "audience-explorer" } });
  return true;
}

/**
 * Ask Calvin del buscador: abre el chat desde la configuración de búsqueda.
 * Del lado del chat habilita la tool que crea la tarea de Calvin ruteada al
 * subagente `search-result-analyzer` (analiza por qué una query devuelve lo
 * que devuelve y prueba cambios de config en modo debug).
 */
export function askSearchInSupportChat() {
  if (typeof window === "undefined") return false;
  const chat = window[WIDGET_GLOBAL];
  if (!chat || typeof chat.openWith !== "function") return false;
  chat.openWith({ searchAnalyzer: { origin: "search-settings" } });
  return true;
}

/**
 * Deja el widget en el estado que corresponde a la sesión actual. Se llama en
 * cada navegación (router.afterEach, ver main.js): login, logout y el cambio de
 * compañía o de proyecto siempre pasan por el router. Es idempotente, así que
 * navegar dentro del admin con el chat ya montado no hace nada.
 */
export function syncSupportChat(options = {}) {
  if (!isSupportChatEnabled()) {
    unmountSupportChat();
    return;
  }

  mountSupportChat(options);
  reloadEmbedIfStale();
  applyLauncherHidden();
  clearSupportChatContextOnScreenChange(options.screen);
  setSupportChatScreen(options.screen);
}

// Última pantalla informada. El contexto (una variante, un componente, una
// integración) lo pone un click explícito de "Ask Calvin" y no navega, así que
// un cambio de ruta significa que el usuario se fue de donde lo puso.
let lastReportedScreen;

/**
 * Suelta el contexto del chat cuando el usuario cambia de pantalla.
 *
 * Sin esto la variante quedaba pegada: abrías Ask Calvin en una variante, te
 * ibas a otra pantalla y el chat seguía hablando de ella, con las herramientas
 * de Calvin apuntándole. No abre el panel ni interrumpe nada; si el chat estaba
 * abierto, simplemente vuelve a ser el chat general.
 */
function clearSupportChatContextOnScreenChange(screen) {
  const next = screen || null;
  const previous = lastReportedScreen;
  lastReportedScreen = next;

  // La primera navegación no es un cambio: no hay nada que soltar.
  if (previous === undefined || previous === next) return;

  const chat = window[WIDGET_GLOBAL];
  // Widget viejo cacheado: sin clearContext todo sigue como antes.
  if (!chat || typeof chat.clearContext !== "function") return;
  chat.clearContext();
}

// Última pantalla informada, haya widget o no.
let reportedScreen = null;

/**
 * Le dice al chat en qué pantalla del admin está el usuario (el nombre de la
 * ruta). Lo usa para las preguntas de ejemplo: sin esto sugiere lo genérico.
 * El widget lo recuerda y lo reenvía cuando el iframe termina de cargar, así
 * que no importa si el panel todavía no se abrió.
 *
 * Sólo viaja el nombre de la ruta — nada de ids ni datos de la pantalla.
 */
export function setSupportChatScreen(screen) {
  if (typeof window === "undefined") return;

  // Se recuerda exista o no el widget: en la carga inicial el router avisa la
  // pantalla en el mismo tick en que se inyecta el script, o sea antes de que
  // exista nadie a quien avisarle. El onload la reenvía desde acá.
  reportedScreen = screen || null;

  const chat = window[WIDGET_GLOBAL];
  // Widget viejo cacheado: sin setScreen el chat sigue andando con las
  // sugerencias genéricas.
  if (!chat || typeof chat.setScreen !== "function") return;
  chat.setScreen(reportedScreen);
}

/**
 * URL del chat para embeberlo adentro de una pantalla (no flotante): el mismo
 * /embed con las credenciales de siempre y `mode=inline`, que esconde los
 * botones de minimizar/maximizar. Lo usa la vista "Pedile a Calvin" del
 * buscador. Sin credenciales devuelve null (el chat no tendría proyecto).
 */
export function supportChatEmbedUrl() {
  const credentials = readCredentials();
  if (!credentials.token || !credentials.projectId) return null;
  const url = withCredentials(`${getSupportChatOrigin()}/embed`, credentials);
  try {
    const parsed = new URL(url);
    parsed.searchParams.set("mode", "inline");
    return parsed.href;
  } catch (e) {
    return null;
  }
}
